import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaSave } from 'react-icons/fa';
import api from '../../utils/api.service';
import { ENDPOINTS } from '../../utils/api-config';
import { toast } from 'react-toastify';
import './Services.css';

const EditService = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    categoryId: '',
    description: '',
    basePrice: '',
    durationMinutes: '',
    isPopular: false,
    isActive: true,
    pricingType: 'hourly',
    providerId: ''
  });

  const [categories, setCategories] = useState([]);
  const [providers, setProviders] = useState([]);
  const [isConfinement, setIsConfinement] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.get(ENDPOINTS.SERVICES.CATEGORIES);
        setCategories(response.data || []);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    const fetchProviders = async () => {
      try {
        const response = await api.get(`${ENDPOINTS.PROVIDERS.base}?status=all&limit=200`);
        console.log('🚀 [EditService] Providers Response:', response);
        const providersList = response.data?.data || response.data || [];
        console.log('📦 [EditService] Providers List:', providersList);
        setProviders(providersList);
      } catch (error) {
        console.error('Failed to fetch providers:', error);
      }
    };
    fetchCategories();
    fetchProviders();
  }, []);

  useEffect(() => {
    const fetchService = async () => {
      try {
        const response = await api.get(`${ENDPOINTS.SERVICES.base}/${id}`);
        const data = response.data;
        console.log('🔍 [EditService] Service Data:', data);
        setFormData({
          name: data.name,
          categoryId: data.categoryId || (data.category && data.category.id) || '',
          description: data.description,
          basePrice: data.basePrice,
          durationMinutes: data.durationMinutes || '',
          isPopular: data.isPopular,
          isActive: data.isActive,
          pricingType: data.pricingType || 'hourly',
          providerId: data.creatorId || (data.providers && data.providers.length > 0 ? data.providers[0].id : '')
        });
      } catch (error) {
        console.error('Failed to fetch service:', error);
        toast.error('Failed to load service data');
        navigate('/services');
      } finally {
        setLoading(false);
      }
    };

    fetchService();
  }, [id, navigate]);

  useEffect(() => {
    const selectedCat = categories.find(c => c.id === parseInt(formData.categoryId));
    const isConfinementCat = selectedCat && (
      selectedCat.name?.toLowerCase().includes('confinement') || 
      selectedCat.slug?.toLowerCase().includes('confinement')
    );
    console.log('🧐 [EditService] Detection:', { categoryId: formData.categoryId, selectedCat, isConfinementCat });

    if (isConfinementCat) {
      setIsConfinement(true);
      if (formData.pricingType !== 'monthly') {
        console.log('🔄 [EditService] Switching to MONTHLY for Confinement');
        setFormData(prev => ({ ...prev, pricingType: 'monthly' }));
      }
    } else {
      setIsConfinement(false);
      // For any other category, we force it to Hourly to stay consistent with new requirements.
      if (formData.pricingType !== 'hourly' && categories.length > 0) {
        console.log('🔄 [EditService] Switching to HOURLY for standard category');
        setFormData(prev => ({ ...prev, pricingType: 'hourly' }));
      }
    }
  }, [formData.categoryId, categories]);

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        const payload = {
          ...formData,
          categoryId: parseInt(formData.categoryId),
          basePrice: parseFloat(formData.basePrice),
          durationMinutes: formData.pricingType === 'hourly' ? parseInt(formData.durationMinutes) : null,
          providerId: formData.providerId ? parseInt(formData.providerId) : null,
        };
  
        console.log('📤 [EditService] Sending Update Payload:', payload);
        await api.patch(`${ENDPOINTS.SERVICES.base}/${id}`, payload);
      toast.success('Service updated successfully!');
      navigate('/services');
    } catch (error) {
      console.error('Failed to update service:', error);
      toast.error('Failed to update service: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };

  const getPriceLabel = () => {
    if (isConfinement) return 'Monthly Package Price ($) *';
    switch (formData.pricingType) {
      case 'daily': return 'Daily Package Price ($) *';
      case 'weekly': return 'Weekly Package Price ($) *';
      case 'monthly': return 'Monthly Package Price ($) *';
      default: return 'Base Price ($/hr) *';
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loader"></div>
        <p>Loading service details...</p>
      </div>
    );
  }

  return (
    <div className="create-service-page">
      <div className="page-header">
        <button onClick={() => navigate('/services')} className="btn btn-secondary">
          <FaArrowLeft /> Back to Services
        </button>
      </div>

      <div className="content-card">
        <h3 className="card-title">Edit Service: {formData.name}</h3>
        
        <form onSubmit={handleSubmit} className="service-form">
          <div className="form-row">
            <div className="form-group">
              <label>Service Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Nanny Service"
                className="form-control"
                required
              />
            </div>
            <div className="form-group">
              <label>Category *</label>
              <select
                name="categoryId"
                value={formData.categoryId}
                onChange={handleChange}
                className="form-control"
                required
              >
                <option value="">Select Category</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Assign Provider (Optional)</label>
              <select
                name="providerId"
                value={formData.providerId}
                onChange={handleChange}
                className="form-control"
              >
                <option value="">Choose Provider</option>
                {providers.map((provider) => (
                  <option key={provider.id} value={provider.id}>
                    {provider.firstName} {provider.lastName}
                  </option>
                ))}
              </select>
              <p className="form-hint">Updating the provider will link this service to their profile immediately.</p>
            </div>
            <div className="form-group">
               {/* Spacer */}
            </div>
          </div>

          <div className="form-group">
            <label>Description *</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Describe the service..."
              rows="4"
              className="form-control"
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Service Pricing Model *</label>
              <select
                name="pricingType"
                value={formData.pricingType}
                onChange={handleChange}
                className="form-control"
                required
              >
                {!isConfinement ? (
                  <option value="hourly">Hourly Billing (Standard)</option>
                ) : (
                  <option value="monthly">Monthly Package (30 Days)</option>
                )}
              </select>
              {!isConfinement && formData.categoryId && <p className="form-hint">This category is restricted to Hourly model.</p>}
              {isConfinement && <p className="form-hint">Confinement Nanny services are based on Packages.</p>}
            </div>
            <div className="form-group">
               {/* Empty placeholder for alignment */}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>{getPriceLabel()}</label>
              <input
                type="number"
                name="basePrice"
                value={formData.basePrice}
                onChange={handleChange}
                placeholder="e.g., 30.00"
                step="0.01"
                className="form-control"
                required
              />
            </div>
            {/* Typical Duration field hidden as per user request */}
          </div>

          <div className="form-group mt-3">
            <div className="toggle-switch-container">
              <span className="toggle-label-text">Service is Active</span>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => navigate('/services')}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              <FaSave /> Update Service
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditService;
